/** @jsxImportSource @emotion/react */
import React from 'react';
import { css } from '@emotion/react';
import { Card, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { PlanAppMode, PlanningSelectionInfo, PlanningActionType } from '../../types/planManagement';
import ModeSegment from './planEditor/toolbar/ModeSegment';
import PlanningModeContent from './planEditor/toolbar/PlanningModeContent';
import PlantingModeContent from './planEditor/toolbar/PlantingModeContent';

const { Text } = Typography;

const styles = {
  toolbar: css({ marginBottom: 12 }),
};

interface SelectionRect {
  rows: number;
  cols: number;
  occupiedCells: string[];
  exceedsBoundary?: boolean;
  maxBoundingBox?: number;
  minRow: number;
  maxRow: number;
  minCol: number;
  maxCol: number;
}

interface PlanToolbarProps {
  appMode: PlanAppMode;
  isBusy: boolean;
  allowBedSelection: boolean;
  disablePlanningMode: boolean;
  disablePlantingMode: boolean;
  disableManagementMode?: boolean;

  // Planning mode — select-then-act
  planningSelection: PlanningSelectionInfo | null;
  selectionRect: SelectionRect | null;

  // Pro (planting) mode
  isEraseMode: boolean;
  multiSelectedBeds: Set<string>;
  isBulkPlacementMode?: boolean;
  bulkGridDimensions?: { rows: number; cols: number } | null;

  // Callbacks
  onAppModeChange: (mode: PlanAppMode) => void;
  onPlanningAction: (action: PlanningActionType) => void;
  onCancelPlanningSelection: () => void;
  onToggleEraseMode: () => void;
  onOpenBulkPlacementModal?: () => void;
  onCancelBulkPlacement?: () => void;
  onBatchAction?: () => void;
}

const PlanToolbar: React.FC<PlanToolbarProps> = ({
  appMode,
  isBusy,
  allowBedSelection,
  disablePlanningMode,
  disablePlantingMode,
  disableManagementMode = false,
  planningSelection,
  selectionRect,
  isEraseMode,
  multiSelectedBeds,
  isBulkPlacementMode,
  bulkGridDimensions,
  onAppModeChange,
  onPlanningAction,
  onCancelPlanningSelection,
  onToggleEraseMode,
  onOpenBulkPlacementModal,
  onCancelBulkPlacement,
  onBatchAction,
}) => {
  const { t } = useTranslation('planning');
  return (
    <Card size="small" css={styles.toolbar}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <ModeSegment
          appMode={appMode}
          disablePlanningMode={disablePlanningMode}
          disablePlantingMode={disablePlantingMode}
          disableManagementMode={disableManagementMode}
          onAppModeChange={onAppModeChange}
        />

        {appMode === 'planning' && (
          <PlanningModeContent
            allowBedSelection={allowBedSelection}
            isBusy={isBusy}
            planningSelection={planningSelection}
            selectionRect={selectionRect}
            onPlanningAction={onPlanningAction}
            onCancelPlanningSelection={onCancelPlanningSelection}
          />
        )}

        {appMode === 'planting' && (
          <PlantingModeContent
            isBusy={isBusy}
            isEraseMode={isEraseMode}
            multiSelectedBeds={multiSelectedBeds}
            isBulkPlacementMode={isBulkPlacementMode}
            bulkGridDimensions={bulkGridDimensions}
            onToggleEraseMode={onToggleEraseMode}
            onOpenBulkPlacementModal={onOpenBulkPlacementModal}
            onCancelBulkPlacement={onCancelBulkPlacement}
            onBatchAction={onBatchAction}
          />
        )}

        {appMode === 'management' && (
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Text type="secondary">
              {t('toolbar.managementHint')}
            </Text>
          </Space>
        )}
      </Space>
    </Card>
  );
};

export default PlanToolbar;
